import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Loader2 } from "lucide-react";

const DEFAULT_AMOUNTS = [5, 10, 25];

/**
 * TipButton — renders next to forum posts and replies. Sends a $CROWN tip
 * to the post/reply author. Backend enforces all the safety rules:
 *   - same-program only
 *   - non-staff sender + recipient
 *   - recipient must have tips enabled
 *   - daily cap
 *   - cannot tip yourself
 *
 * This component intentionally stays "thin" — it doesn't try to predict
 * whether the user is allowed to tip. It just disables itself when we know
 * up-front (own post, staff, opposite program) to reduce UI noise.
 */
export default function TipButton({
  postId,
  replyId,
  recipientId,
  recipientProgram,
  recipientRole,
  recipientAnonymous,
  recipientName,
  currentUser,
  onSuccess,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(null);

  // Hard guards we can determine locally — match backend rules
  const sameUser = currentUser?.id === recipientId;
  const isStaff = currentUser && currentUser.role !== "participant" && currentUser.role !== "admin";
  const recipientIsStaff = recipientRole && recipientRole !== "participant" && recipientRole !== "admin";
  const programMismatch =
    recipientProgram &&
    currentUser?.program &&
    recipientProgram !== currentUser.program &&
    currentUser.role !== "admin" &&
    recipientRole !== "admin";

  if (sameUser || isStaff || recipientIsStaff || programMismatch) {
    return null;
  }

  const send = async (amount) => {
    setBusy(true);
    setPicked(amount);
    try {
      const body = { post_id: postId, amount };
      if (replyId) body.reply_id = replyId;
      const { data } = await api.post("/wallet/tip", body);
      toast.success(`+${amount} $CROWN sent. Wisdom acknowledged.`, {
        description: `New balance: ${data.new_balance} $CROWN`,
      });
      setOpen(false);
      if (onSuccess) onSuccess(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Couldn't send the tip.";
      toast.error(typeof detail === "string" ? detail : "Couldn't send the tip.");
    } finally {
      setBusy(false);
      setPicked(null);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs text-[#C9A961] hover:text-[#E2C97A] transition"
        data-testid={replyId ? `tip-reply-btn-${replyId}` : `tip-post-btn-${postId}`}
        title={`Send Wisdom tip to ${recipientAnonymous ? recipientName : (recipientName || "this participant")}`}
      >
        <Heart className="w-3 h-3" />
        Tip Wisdom
      </button>
    );
  }

  return (
    <div
      className="inline-flex items-center gap-2 bg-[#1A1816] border border-[#C9A961]/30 rounded-md px-3 py-2"
      data-testid={replyId ? `tip-panel-reply-${replyId}` : `tip-panel-post-${postId}`}
    >
      <span className="text-[10px] text-[#8A8782] uppercase tracking-wider mr-1">Tip</span>
      {DEFAULT_AMOUNTS.map((amt) => (
        <button
          key={amt}
          type="button"
          disabled={busy}
          onClick={() => send(amt)}
          data-testid={`tip-amount-${amt}`}
          className="px-2 py-1 text-xs rounded border border-[#C9A961]/40 text-[#C9A961] hover:bg-[#C9A961]/10 disabled:opacity-50 transition"
        >
          {busy && picked === amt ? <Loader2 className="w-3 h-3 animate-spin" /> : `${amt}`}
        </button>
      ))}
      <button
        type="button"
        onClick={() => setOpen(false)}
        disabled={busy}
        className="text-[10px] text-[#8A8782] hover:text-[#D1CFC9] ml-1"
        data-testid="tip-cancel"
      >
        cancel
      </button>
    </div>
  );
}
